import { InputSelectionListChangeEvent } from '@dixa/lib-shared-habla';
import isEqual from 'lodash/isEqual';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDeepCompareEffect } from 'react-use';

import {
  CustomFieldSelectAttribute,
  CustomFieldStringListAttributeValue,
} from 'apis/customFields';
import { CustomFieldSuggestionDropdownButton } from 'components/conversation/conversationCustomFieldsForm/components/suggestions/customFieldSuggestionDropdownButton';
import { BaseCustomFieldSelect } from 'components/customFields/baseCustomFieldSelect/baseCustomFieldSelect';
import { errorNotification } from 'components/customFields/utils/notification';
import { useNotifications } from 'hooks/useNotifications';

import { ConversationCustomFieldBaseProps } from '../../conversationCustomFieldsForm.types';
import { getConversationCustomFieldsFormWarningMessage } from '../../utils/conversationCustomFieldsFormWarningMessage';
import { useSaveStatusIcon } from '../../utils/useSaveStatusIcon';

type CustomFieldSelectProps = ConversationCustomFieldBaseProps<
  CustomFieldSelectAttribute,
  CustomFieldStringListAttributeValue
>;

/**
 * Renders a hook form select field to display/edit a custom field of type
 * select.
 */
export const CustomFieldSelect = memo(function CustomFieldSelect({
  isAcw,
  customField,
  save,
  suggestions,
}: CustomFieldSelectProps): JSX.Element {
  const { attribute, value } = customField;
  const isRequired = attribute.is_required;
  const { id, data_type } = attribute;
  const notify = useNotifications();
  const [icon, setStatus] = useSaveStatusIcon();
  const savedValue = useMemo(
    () => (value?.value ? [value?.value] : undefined),
    [value?.value],
  );
  const formSavedValues = useMemo(
    () => ({ [id]: savedValue }),
    [id, savedValue],
  );
  const { control, getFieldState, reset, setValue } = useForm({
    mode: 'onChange',
    defaultValues: formSavedValues,
    shouldFocusError: false,
  });

  useEffect(() => {
    if (isAcw) {
      // A trick to make the field `touched` and `dirty`, so that the warning
      // will be displayed when in wrap-up mode.
      // Using setTimeout because sometimes the field is not already rendered
      setTimeout(() => {
        setValue(id, savedValue, {
          shouldDirty: true,
          shouldTouch: true,
        });
      });
    }
  }, [id, isAcw, savedValue, setValue]);

  const changeValue = useCallback(
    async (newValue: string[][]) => {
      const fieldState = getFieldState(id);
      if (!fieldState.error && !isEqual(savedValue, newValue)) {
        const patch = {
          before: value?.value
            ? {
                [id]: {
                  data_type,
                  value: value?.value,
                },
              }
            : {},
          after: newValue.length
            ? {
                [id]: {
                  data_type,
                  value: newValue[0],
                },
              }
            : {},
        };

        setStatus('none');
        try {
          await save(attribute, patch);
          setStatus('success');
        } catch {
          reset(formSavedValues);
          setStatus('error');
          notify(errorNotification);
        }
      }
    },
    [
      attribute,
      data_type,
      formSavedValues,
      getFieldState,
      id,
      notify,
      reset,
      save,
      savedValue,
      setStatus,
      value?.value,
    ],
  );

  const handleChange = useCallback(
    async (changeEvent: InputSelectionListChangeEvent<string[]>) => {
      return changeValue(changeEvent.selectedValues);
    },
    [changeValue],
  );

  useDeepCompareEffect(() => {
    reset(formSavedValues);
  }, [formSavedValues]);

  return (
    <BaseCustomFieldSelect
      customField={customField}
      rules={{ required: isRequired }}
      componentProps={{
        icon,
        prefix: !savedValue &&
          suggestions?.length && {
            type: 'custom',
            noWrap: true,
            value: (
              <CustomFieldSuggestionDropdownButton
                suggestions={suggestions}
                setSuggestion={async (value) => {
                  setValue(id, [value], {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: true,
                  });
                  return changeValue([value]);
                }}
              />
            ),
          },
      }}
      control={control}
      warning={getConversationCustomFieldsFormWarningMessage(
        customField,
        isAcw,
      )}
      onChange={handleChange}
    />
  );
});
